import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

export interface ZoneItem {
  zoneId: string;
  zoneName: string;
  accountId: string;
  accountName: string;
  planName: string;
  type: string;
  status: string;
}

export interface CreateZoneItemPayload {
  name: string;
}

interface ZoneState {
  list: ZoneItem[];
  isListFetched: boolean;
  isItemFetched: boolean;
  activePage: number;
  totalItems: number;
  totalPages: number;
  selectedItems: ZoneItem[];
  draftItem?: ZoneItem;
  processingItemId?: string;
}

interface GetListResponse {
  items: ZoneItem[];
}

const getZoneList = createAsyncThunk("zones/list", async () => {
  try {
    const result = await instance.get(`${apiRoutes.zonesList}`);
    return result.data as GetListResponse;
  } catch (error) {
    return undefined;
  }
});

const getZoneItem = createAsyncThunk("zones/item", async (zoneName: string) => {
  try {
    const result = await instance.get(`${apiRoutes.zoneItem}/${zoneName}`);
    return result.data as ZoneItem;
  } catch (error) {
    return undefined;
  }
});

const deleteZoneItem = createAsyncThunk(
  "zones/delete",
  async (zoneName: string) => {
    try {
      await instance.delete(`${apiRoutes.zoneItem}/${zoneName}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

const initialState: ZoneState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 0,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
};

const zonesSlice = createSlice({
  name: "zones",
  initialState,
  reducers: {
    handleClearFields: (state) => {
      state.draftItem = undefined;
      state.isItemFetched = false;
    },
    handleUpdateFieldDraftItem: (
      state,
      payload: PayloadAction<Partial<ZoneItem>>,
    ) => {
      state.draftItem = {
        ...(state.draftItem ?? ({} as ZoneItem)),
        ...payload.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getZoneList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];

      state.totalPages = 1;
      state.totalItems = action.payload?.items.length ?? 1;
    }),
      builder.addCase(getZoneList.pending, (state) => {
        state.isListFetched = false;
      });
    builder.addCase(getZoneList.rejected, (state) => {
      state.isListFetched = true;
    });
    builder.addCase(getZoneItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.draftItem = action.payload;
    }),
      builder.addCase(deleteZoneItem.pending, (state, action) => {
        state.processingItemId = action.meta.arg;
      });

    builder.addCase(deleteZoneItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });

    builder.addCase(deleteZoneItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
  },
});

const { handleClearFields, handleUpdateFieldDraftItem } = zonesSlice.actions;

export {
  zonesSlice,
  getZoneList,
  getZoneItem,
  handleClearFields,
  handleUpdateFieldDraftItem,
  deleteZoneItem,
};
